import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Search,
  MoreVertical,
  Eye,
  Ban,
  CheckCircle,
  Trash2,
  Mail,
  User as UserIcon,
  LogIn,
  ArrowUpDown,
  Sparkles,
  Activity,
  Filter,
  XCircle
} from 'lucide-react';
import { adminService } from '../../services/adminService';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import AssignPlanModal from '../../components/admin/AssignPlanModal';
import VideoCreditsModal from '../../components/admin/VideoCreditsModal';
import AiCreditsModal from '../../components/admin/AiCreditsModal';
import UserAuditLogModal from '../../components/admin/UserAuditLogModal';
import { Plan, Subscription, User } from '../../types';

interface AdminUser extends User {
  subscriptions?: (Subscription & { plan?: Plan })[];
}

// Explicit i18n maps. Computing key names from role/status with
// `replace('_','')` produces wrong keys (e.g. super_admin → roleSuperadmin
// instead of roleSuperAdmin) — show the literal key in the UI when missed.
const ROLE_KEY: Record<string, string> = {
  user: 'admin.roleUser',
  admin: 'admin.roleAdmin',
  super_admin: 'admin.roleSuperAdmin'
};
const STATUS_KEY: Record<string, string> = {
  active: 'admin.statusActive',
  pending: 'admin.statusPending',
  suspended: 'admin.statusSuspended',
  inactive: 'admin.statusSuspended'
};

const AdminUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isVideoCreditsModalOpen, setIsVideoCreditsModalOpen] = useState(false);
  const [isAiCreditsModalOpen, setIsAiCreditsModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  // Debounced search and reactive filters
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, roleFilter, statusFilter, planFilter, createdFrom, createdTo, search]);

  const loadPlans = async () => {
    try {
      const response = await adminService.getPlans();
      if (response.success && response.data) setPlans(response.data);
    } catch { /* noop */ }
  };

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const response = await adminService.getUsers({
        page,
        limit: 15,
        search: search || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        plan_id: planFilter || undefined,
        created_from: createdFrom || undefined,
        created_to: createdTo || undefined
      });
      if (response.success && response.data) {
        setUsers(response.data as AdminUser[]);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
    setStatusFilter('');
    setPlanFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    setPage(1);
  };

  const handleViewUser = (user: AdminUser) => {
    setSelectedUser(user);
    setIsDetailModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleManagePlan = (user: AdminUser) => {
    setSelectedUser(user);
    setIsAssignModalOpen(true);
    setIsActionMenuOpen(null);
  };

  // Créditos de vídeo viraram créditos de IA (04/11/2026): o menu usa só handleAiCredits
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleVideoCredits = (user: AdminUser) => {
    setSelectedUser(user);
    setIsVideoCreditsModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleAiCredits = (user: AdminUser) => {
    setSelectedUser(user);
    setIsAiCreditsModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleAuditLog = (user: AdminUser) => {
    setSelectedUser(user);
    setIsAuditModalOpen(true);
    setIsActionMenuOpen(null);
  };

  const handleToggleStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await adminService.updateUserStatus(userId, newStatus);
      toast.success(t('messages.updatedSuccessfully'));
      loadUsers();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
    setIsActionMenuOpen(null);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm(t('messages.confirmDelete'))) return;
    try {
      await adminService.deleteUser(userId);
      toast.success(t('messages.deletedSuccessfully'));
      loadUsers();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
    setIsActionMenuOpen(null);
  };

  // Impersonate: salva o token do admin atual em `admin_token`/`admin_user`
  // (pra retornar depois) e troca pelo token do usuário-alvo. Hard-redirect
  // pra `/dashboard` pra resetar todos os contextos React (ProjectContext, etc).
  const handleImpersonate = async (userId: string, userEmail?: string) => {
    setIsActionMenuOpen(null);
    try {
      const res = await adminService.impersonateUser(userId);
      if (res.success && res.data) {
        localStorage.setItem('admin_token', localStorage.getItem('token') || '');
        localStorage.setItem('admin_user', localStorage.getItem('user') || '');
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.removeItem('currentProjectId');
        toast.success(t('admin.impersonating', 'Acessando como {{email}}', { email: userEmail || res.data.user.email }));
        window.location.href = '/dashboard';
      }
    } catch {
      toast.error(t('admin.impersonateError', 'Erro ao acessar como usuário'));
    }
  };

  const handleSendEmail = async (userId: string) => {
    try {
      await adminService.sendUserEmail(userId, { subject: 'Notification', template: 'admin_notification' });
      toast.success(t('admin.emailSent'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
    setIsActionMenuOpen(null);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'gray'> = {
      active: 'success',
      pending: 'warning',
      suspended: 'danger',
      inactive: 'gray'
    };
    return variants[status] || 'gray';
  };

  const getRoleBadge = (role: string) => {
    const variants: Record<string, 'primary' | 'warning' | 'gray'> = {
      super_admin: 'primary',
      admin: 'warning',
      user: 'gray'
    };
    return variants[role] || 'gray';
  };

  const activeSubFor = (u: AdminUser): (Subscription & { plan?: Plan }) | undefined =>
    u.subscriptions?.find(s => s.status === 'active' || s.status === 'trialing') || u.subscription as any;

  const filtersAreActive = useMemo(() =>
    !!(search || roleFilter || statusFilter || planFilter || createdFrom || createdTo),
  [search, roleFilter, statusFilter, planFilter, createdFrom, createdTo]);

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('admin.users')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('admin.manageUsers')}</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row gap-3">
              <Input
                wrapperClassName="flex-1"
                placeholder={t('admin.searchUsers')}
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                leftIcon={<Search className="w-5 h-5" />}
              />
              <Select
                wrapperClassName="w-full md:w-44"
                options={[
                  { value: '', label: t('admin.allRoles') },
                  { value: 'user', label: t('admin.roleUser') },
                  { value: 'admin', label: t('admin.roleAdmin') },
                  { value: 'super_admin', label: t('admin.roleSuperAdmin') }
                ]}
                value={roleFilter}
                onChange={(e) => { setPage(1); setRoleFilter(e.target.value); }}
              />
              <Select
                wrapperClassName="w-full md:w-44"
                options={[
                  { value: '', label: t('admin.allStatus') },
                  { value: 'active', label: t('admin.statusActive') },
                  { value: 'pending', label: t('admin.statusPending') },
                  { value: 'suspended', label: t('admin.statusSuspended') }
                ]}
                value={statusFilter}
                onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
              />
              <Button variant="ghost" onClick={() => setShowAdvanced((v) => !v)} className="md:w-auto">
                <Filter className="w-4 h-4 mr-2" />
                {t('admin.filterByPlan')}
              </Button>
              {filtersAreActive && (
                <Button variant="secondary" onClick={clearFilters} className="md:w-auto">
                  <XCircle className="w-4 h-4 mr-2" />
                  {t('common.clear', 'Limpar')}
                </Button>
              )}
            </div>
            {showAdvanced && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                <Select
                  wrapperClassName="w-full"
                  label={t('admin.filterByPlan')}
                  options={[
                    { value: '', label: t('admin.allPlans') },
                    ...plans.map(p => ({ value: p.id, label: p.name }))
                  ]}
                  value={planFilter}
                  onChange={(e) => { setPage(1); setPlanFilter(e.target.value); }}
                />
                <Input
                  wrapperClassName="w-full"
                  label={t('admin.createdFrom')}
                  type="date"
                  value={createdFrom}
                  onChange={(e) => { setPage(1); setCreatedFrom(e.target.value); }}
                />
                <Input
                  wrapperClassName="w-full"
                  label={t('admin.createdTo')}
                  type="date"
                  value={createdTo}
                  onChange={(e) => { setPage(1); setCreatedTo(e.target.value); }}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card padding={false}>
        <div className="overflow-x-auto scrollbar-hide">
          {isLoading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 skeleton rounded-lg" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.user')}</th>
                  <th>{t('admin.company')}</th>
                  <th>{t('admin.role')}</th>
                  <th>{t('admin.status')}</th>
                  <th>{t('admin.plan')}</th>
                  <th>{t('admin.createdAt')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const sub = activeSubFor(user);
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-medium">{(user.name || user.email).charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          {(user as any).company_name ? (
                            <span className="text-gray-900 dark:text-white">{(user as any).company_name}</span>
                          ) : (user as any).belongs_to_company ? (
                            <span className="text-gray-500 dark:text-gray-400 italic">{(user as any).belongs_to_company} <span className="text-xs">(employee)</span></span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge variant={getRoleBadge(user.role)}>
                          {t(ROLE_KEY[user.role] || 'admin.roleUser')}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={getStatusBadge(user.status || 'active')}>
                          {t(STATUS_KEY[(user.status || 'active') as string] || 'admin.statusActive')}
                        </Badge>
                      </td>
                      <td>
                        {sub ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-gray-900 dark:text-white">{sub.plan?.name || '-'}</span>
                            {sub.metadata?.is_manual && (
                              <Badge variant="warning">{t('admin.manualSubscription')}</Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">{t('admin.noPlanShort')}</span>
                        )}
                      </td>
                      <td className="text-gray-500 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td>
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => handleImpersonate(user.id, user.email)}
                            title={t('admin.impersonate', 'Acessar como') as string}
                            className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                          >
                            <LogIn className="w-4 h-4" />
                          </button>
                          <button
                            data-user-id={user.id}
                            onClick={() => setIsActionMenuOpen(isActionMenuOpen === user.id ? null : user.id)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-700">
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              {t('common.previous')}
            </Button>
            <span className="text-sm text-gray-600 dark:text-gray-400">{page} / {totalPages}</span>
            <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              {t('common.next')}
            </Button>
          </div>
        )}
      </Card>

      {/* Action Menu */}
      {isActionMenuOpen && (() => {
        const user = users.find(u => u.id === isActionMenuOpen);
        if (!user) return null;
        return (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsActionMenuOpen(null)} />
            <div
              className="fixed z-50 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1"
              style={{
                top: document.querySelector(`[data-user-id="${isActionMenuOpen}"]`)?.getBoundingClientRect().bottom ?? 0,
                left: Math.min(
                  (document.querySelector(`[data-user-id="${isActionMenuOpen}"]`)?.getBoundingClientRect().right ?? 0) - 224,
                  window.innerWidth - 232
                )
              }}
            >
              <button onClick={() => handleViewUser(user)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <Eye className="w-4 h-4" />
                {t('common.view')}
              </button>
              <button onClick={() => handleManagePlan(user)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <ArrowUpDown className="w-4 h-4" />
                {t('admin.managePlan')}
              </button>
              <button onClick={() => handleAiCredits(user)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <Sparkles className="w-4 h-4" />
                {t('admin.aiCreditsTitle', 'Créditos de IA')}
              </button>
              <button onClick={() => handleAuditLog(user)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <Activity className="w-4 h-4" />
                {t('admin.viewAuditLog')}
              </button>
              <button onClick={() => handleSendEmail(user.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                <Mail className="w-4 h-4" />
                {t('admin.sendEmail')}
              </button>
              <button onClick={() => handleToggleStatus(user.id, user.status || 'active')} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                {user.status === 'active' ? (
                  <><Ban className="w-4 h-4" />{t('admin.suspend')}</>
                ) : (
                  <><CheckCircle className="w-4 h-4" />{t('admin.activate')}</>
                )}
              </button>
              <button onClick={() => handleImpersonate(user.id, user.email)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20">
                <LogIn className="w-4 h-4" />
                {t('admin.impersonate')}
              </button>
              <hr className="my-1 border-gray-200 dark:border-gray-700" />
              <button onClick={() => handleDeleteUser(user.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:text-red-400">
                <Trash2 className="w-4 h-4" />
                {t('common.delete')}
              </button>
            </div>
          </>
        );
      })()}

      {/* User Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={t('admin.userDetails')}
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                <span className="text-2xl text-primary-600 font-bold">
                  {(selectedUser.name || selectedUser.email).charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedUser.name}</h3>
                <p className="text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant={getRoleBadge(selectedUser.role)}>
                    {t(ROLE_KEY[selectedUser.role] || 'admin.roleUser')}
                  </Badge>
                  <Badge variant={getStatusBadge(selectedUser.status || 'active')}>
                    {t(STATUS_KEY[(selectedUser.status || 'active') as string] || 'admin.statusActive')}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.company')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{selectedUser.company || (selectedUser as any).company_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.plan')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{activeSubFor(selectedUser)?.plan?.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.createdAt')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {new Date(selectedUser.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.lastLogin')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedUser.last_login_at
                    ? new Date(selectedUser.last_login_at).toLocaleString('pt-BR')
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.emailVerified')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedUser.email_verified_at ? (
                    <span className="text-green-600 dark:text-green-400">{t('common.yes')}</span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400">{t('common.no')}</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.subscriptionStatus')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {activeSubFor(selectedUser)?.status
                    ? t(`subscription.status.${activeSubFor(selectedUser)!.status}`)
                    : '-'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="secondary" onClick={() => setIsDetailModalOpen(false)}>
                {t('common.close')}
              </Button>
              <Button variant="ghost" onClick={() => { setIsDetailModalOpen(false); handleAuditLog(selectedUser); }}>
                <Activity className="w-4 h-4 mr-2" />
                {t('admin.viewAuditLog')}
              </Button>
              <Button variant="ghost" onClick={() => { setIsDetailModalOpen(false); handleAiCredits(selectedUser); }}>
                <Sparkles className="w-4 h-4 mr-2" />
                {t('admin.aiCreditsTitle', 'Créditos de IA')}
              </Button>
              <Button onClick={() => { setIsDetailModalOpen(false); handleManagePlan(selectedUser); }}>
                <ArrowUpDown className="w-4 h-4 mr-2" />
                {t('admin.managePlan')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <AssignPlanModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={loadUsers}
        user={selectedUser ? { id: selectedUser.id, email: selectedUser.email, name: selectedUser.name, subscriptions: selectedUser.subscriptions, subscription: activeSubFor(selectedUser) } : undefined}
        plans={plans}
      />

      <VideoCreditsModal
        isOpen={isVideoCreditsModalOpen}
        onClose={() => setIsVideoCreditsModalOpen(false)}
        userId={selectedUser?.id || null}
        userLabel={selectedUser?.email}
      />

      <AiCreditsModal
        isOpen={isAiCreditsModalOpen}
        onClose={() => setIsAiCreditsModalOpen(false)}
        userId={selectedUser?.id || null}
        userLabel={selectedUser?.email}
      />

      <UserAuditLogModal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        userId={selectedUser?.id || null}
        userLabel={selectedUser?.email}
      />
    </div>
  );
};

export default AdminUsersPage;
